import React, { useRef, useState } from 'react';
import './styles.scss';
import Components from './components';
import { useChatStore, useConfigStore } from '@/hooks';
import { useCustomComponent } from '@/hooks';

export interface ChatInputProps {
  onSendMessage: (content: string) => void;
  onTyping: (typing: boolean) => void;
  disabled?: boolean;
  disabledMessage?: string;
  onFocus?: () => void;
}

const ChatInputComponent: React.FC<ChatInputProps> = ({
  onSendMessage,
  onTyping,
  disabled = false,
  disabledMessage,
  onFocus,
}) => {
  const { connectionStatus } = useChatStore();
  const {
    theme: { colors },
  } = useConfigStore();
  const [value, setValue] = useState('');
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    const maxHeight = 120;
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
    textarea.style.overflowY =
      textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };

  const handleFocus = () => {
    // Trigger reconnection if socket is disconnected
    onFocus?.();
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
    autoResize(e.target);

    onTyping(true);
    if (typingTimeout) clearTimeout(typingTimeout);
    const t = setTimeout(() => onTyping(false), 1000);
    setTypingTimeout(t);
  };

  const submit = () => {
    const content = value.trim();
    if (!content || connectionStatus !== 'connected' || disabled) return;
    onSendMessage(content);
    setValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend =
    value.trim().length > 0 && connectionStatus === 'connected' && !disabled;

  return (
    <Components.Container
      className="peptalk-chat-input"
      onSubmit={e => {
        e.preventDefault();
        submit();
      }}
    >
      <Components.Textarea
        ref={textareaRef}
        name="chat-input"
        rows={1}
        value={value}
        placeholder={
          disabled ? disabledMessage || 'Please wait...' : 'Write message...'
        }
        size="large"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        className="peptalk-chat-input-textarea"
        disabled={disabled}
      />
      <Components.SendButton
        type="submit"
        disabled={!canSend}
        className="peptalk-chat-input-send"
        ariaLabel="Send message"
      >
        <div className="-rotate-45">
          <Components.Icon
            name="PaperAirplane"
            color={colors.primary}
            height={16}
            width={16}
          />
        </div>
      </Components.SendButton>
    </Components.Container>
  );
};

export const ChatInput: React.FC<ChatInputProps> = (props: ChatInputProps) => {
  const Component = useCustomComponent({
    componentName: 'ChatInput',
    Component: ChatInputComponent,
  });

  return <Component {...props} />;
};
